import { MediaPreview } from "./MediaPreview";
import { useQuery } from "@tanstack/react-query";
import { api, apiUrl } from "../../lib/api";

type Reference = { asset_id: string; url: string | null; alt: string; mime_type?: string; unresolved?: boolean };

/** Keep the saved draft's stable references intact; resolve only for this preview. */
export function AssetReferencesPreview({ brandId, content }: { brandId: string; content: string }) {
  const hasReferences = /\[\[media:[0-9a-f-]{36}\]\]/i.test(content);
  const preview = useQuery({
    queryKey: ["media-reference-preview", brandId, content],
    queryFn: () => api.post<{ data: { refs: Reference[] } }>(
      `/api/brands/${brandId}/render`, { text: content, context: "plain" },
    ),
    enabled: hasReferences && !!brandId,
    staleTime: 0,
  });
  if (!hasReferences) return null;
  if (preview.isPending) return <p role="status">Loading referenced images…</p>;
  if (preview.isError) return <p role="alert">Referenced images could not be loaded. Retry before approving.</p>;
  const refs = [...new Map(preview.data.data.refs.map((ref) => [ref.asset_id, ref])).values()];
  return <div aria-label="Referenced brand images" className="space-y-3">
    {refs.some((ref) => ref.unresolved || !ref.url) && <p role="alert">A referenced image is missing or was deleted. Update the draft before approving.</p>}
    {refs.filter((ref) => ref.url?.startsWith("/api/public/media/")).map((ref) =>
      <MediaPreview key={ref.asset_id} url={apiUrl(ref.url!)} label={ref.alt} mimeType={ref.mime_type ?? "image/png"} />,
    )}
  </div>;
}
